import { css, html, LitElement } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import '../../library/editors/elements/d-checkbox';
import '../../library/presentational/d-sections.js';
import { headerViewStyles } from 'src/layout/parts/header-view-styles.js';
import { features } from 'src/store/selectors/features/features';
import type { SelectTagOption } from 'src/library/editors/elements/select-tag-option';

/**
 *
 * STATUS OK
 */
@customElement('d-organization-internal-settings')
export class DOrganizationInternalSettings extends LitElement {
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: block;
      }
    `,
  ];
  @property({ type: Array })
  features: string[] = [];
  @state()
  editFeatures: string[] = [];

  private get bundles() {
    return features
      .filter((f) => f.bundle && f.id !== 'EXTRA')
      .map((f) => {
        return { ...f, displayName: f.displayName.replace('Komplett ', '') };
      });
  }

  private get modules() {
    return features.filter((f) => f.module);
  }

  private get nonModuleFeatures() {
    return features.filter((f) => !f.bundle && !f.module && !f.orderOnly);
  }

  private get bundleOptions(): SelectTagOption[] {
    return this.bundles.map((f) => {
      return { value: f.id, text: f.displayName };
    });
  }

  private get moduleOptions(): SelectTagOption[] {
    return this.modules.map((f) => {
      return { value: f.id, text: f.displayName };
    });
  }

  private get nonModuleFeatureOptions() {
    return this.nonModuleFeatures.map((f) => {
      return { value: f.id, text: f.displayName };
    });
  }

  private get bundleValue(): string[] {
    return features
      .filter((f) => f.bundle && this.editFeatures.includes(f.id))
      .map((f) => {
        return f.id;
      });
  }

  private get moduleValue(): string[] {
    return features
      .filter((f) => f.module && this.editFeatures.includes(f.id))
      .map((f) => {
        return f.id;
      });
  }

  private get nonModuleFeatureValue(): string[] {
    return features
      .filter((f) => !f.bundle && !f.module && !f.orderOnly && this.editFeatures.includes(f.id))
      .map((f) => {
        return f.id;
      });
  }

  onBundleChanged(e) {
    e.stopPropagation();
    if (e.detail.value.length) {
      const bundle = this.bundles.find((bundle) => bundle.id === e.detail.value[0]);
      if (bundle && bundle.features) {
        const newFeatures = [...bundle.features];
        newFeatures.push(bundle.id);
        this.editFeatures = newFeatures;
      }
    } else {
      this.editFeatures = [];
    }
  }

  onModuleChanged(e) {
    e.stopPropagation();
    const moduleFeatures = e.detail.value;
    const nonModuleFeatures = this.nonModuleFeatureValue;
    const bundleFeatures = this.bundleValue;

    // Bundles are only selected explicitly via the "Pakker" picker. Toggling a
    // module must not auto-select bundles, so we preserve the currently selected
    // bundle (if any) and never derive bundles from the module selection.
    const newFeatures = [...moduleFeatures, ...nonModuleFeatures, ...bundleFeatures];

    this.editFeatures = newFeatures;
  }

  onNonModuleFeatureChanged(e) {
    e.stopPropagation();
    const nonModuleFeatures = e.detail.value;
    const moduleFeatures = this.moduleValue;
    const bundleFeatures = this.bundleValue;
    // Combine all selected features
    const newFeatures = [...moduleFeatures, ...nonModuleFeatures, ...bundleFeatures];
    this.editFeatures = newFeatures;
  }

  render() {
    return html`
      <div class="settings-section">
        <h2>${t('organization.internalSettingsPackagesAndModules')}</h2>
        <d-select-tag
          outskirts
          wrap-label
          single
          label=${t('organization.internalSettingsPackages')}
          .options=${this.bundleOptions}
          .value=${this.bundleValue}
          @value-changed=${(e) => this.onBundleChanged(e)}
        ></d-select-tag>
        <d-select-tag
          style="margin-top: 12px"
          outskirts
          wrap-label
          label=${t('organization.internalSettingsModules')}
          .options=${this.moduleOptions}
          .value=${this.moduleValue}
          @value-changed=${(e) => this.onModuleChanged(e)}
        ></d-select-tag>
        <d-select-tag
          style="margin-top: 12px"
          outskirts
          wrap-label
          label=${t('organization.internalSettingsFeatures')}
          sublabel=${t('organization.internalSettingsFeaturesSublabel')}
          .options=${this.nonModuleFeatureOptions}
          .value=${this.nonModuleFeatureValue}
          @value-changed=${(e) => this.onNonModuleFeatureChanged(e)}
        ></d-select-tag>
        <div class="bottom-right-buttons">
          <div class="action" @click=${this.updateFeatures}>${t('organization.internalSettingsUpdate')}</div>
        </div>
      </div>
      <div class="settings-section">
        <h2>${t('organization.internalSettingsDevelopment')}</h2>
        <d-checkbox
          outskirts
          wrap-label
          .optionLabel=${t('organization.internalSettingsSimulateMobileApp')}
          .checked=${localStorage.getItem('simulatePhone') === 'TRUE'}
          @checked-changed=${() => {
            if (localStorage.getItem('simulatePhone') === 'TRUE') {
              localStorage.setItem('simulatePhone', 'FALSE');
            } else {
              localStorage.setItem('simulatePhone', 'TRUE');
            }
            window.location.reload();
          }}
        ></d-checkbox>
        <d-checkbox
          outskirts
          wrap-label
          .optionLabel=${t('organization.internalSettingsShowPdfTemplateData')}
          .checked=${localStorage.getItem('showPdfData') === 'TRUE'}
          @checked-changed=${() => {
            if (localStorage.getItem('showPdfData') === 'TRUE') {
              localStorage.setItem('showPdfData', 'FALSE');
            } else {
              localStorage.setItem('showPdfData', 'TRUE');
            }
            window.location.reload();
          }}
        ></d-checkbox>
      </div>
      <div class="settings-section">
        <d-wrap split baseline>
          <h2>${t('organization.internalSettingsAccessControl')}</h2>
          <div>
            <a href="/me" class="action">${t('organization.internalSettingsManageMyAccess')}</a>
          </div>
        </d-wrap>
      </div>
    `;
  }

  protected willUpdate(_changedProperties: PropertyValues) {
    super.willUpdate(_changedProperties);
    if (_changedProperties.has('features')) {
      this.editFeatures = [...this.features];
    }
  }

  private updateFeatures() {
    this.dispatchEvent(
      new CustomEvent<{ features: string[] }>('update-features', {
        composed: true,
        bubbles: true,
        detail: { features: this.editFeatures },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-internal-settings': DOrganizationInternalSettings;
  }
}
